import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { organizationAPI } from '../../services/api';
import { useToast } from '../shared/Toast';
import { DANGER_GHOST_BTN, INPUT_CLS } from '../shared/listPage';
import { SettingsSheet, SheetField, SheetInherit, SheetSection } from '../settings/sheet';
import { SavedNote, SwitchRow, useSavedFlash } from '../settings/instantSettings';
import OrgManualSection from '../OrgManualSection';
import OrgThemeSettings from '../settings/OrgThemeSettings';
import type { StoreTheme } from '../../utils/storeTheme';
import { Organization, serverMessage } from './orgTypes';
import { TFn } from './orgFormat';
import OrgConfirm from './OrgConfirm';

/**
 * «Ρυθμίσεις» των κεντρικών ως «Φύλλο» (14/10/2026): ενότητες με ευρετήριο αντί για το κουτί με τα
 * πεδία. Κάθε αλλαγή αποθηκεύεται ΑΜΕΣΑ, όπως πριν — τα πεδία όταν φύγεις από αυτά, οι επιλογές με το
 * πάτημα — με τις ίδιες κλήσεις (PUT /organizations/:id με το ίδιο σώμα) και την ίδια επαναφόρτωση.
 * Μέσα: εγχειρίδιο χρήσης (OrgManualSection) και η εμφάνιση του οργανισμού (OrgThemeSettings, όπως ήταν).
 * Μόνο ιδιοκτήτης/διαχειριστής φτάνει εδώ· τη διαγραφή του οργανισμού μόνο ο ιδιοκτήτης.
 */

export interface OrgSettingsTabProps {
  org: Organization;
  isOwner: boolean;
  /** Επαναφόρτωση οργανισμών (όπως πριν μετά από όνομα/slug/κατάλογο/ακυρώσεις). */
  onReload: () => Promise<void> | void;
  onThemeSaved: (theme: StoreTheme | null) => void;
  /** Διαγραφή: ο γονέας καθαρίζει την επιλογή και ξαναφορτώνει. */
  onDeleted: () => Promise<void> | void;
}

const OrgSettingsTab: React.FC<OrgSettingsTabProps> = ({ org, isOwner, onReload, onThemeSaved, onDeleted }) => {
  const { t: tRaw } = useTranslation(['orgDashboard', 'admin', 'common', 'settings']);
  const t = tRaw as unknown as TFn;
  const { showToast } = useToast();
  const uid = useId();
  const [saved, flash] = useSavedFlash();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fail = (err: unknown) => showToast('error', serverMessage(err, t('admin:failedUpdate2')));

  // Ίδιες κλήσεις με πριν: όνομα/slug → ενημέρωση + επαναφόρτωση, email/τηλέφωνο → μόνο ενημέρωση.
  const saveName = (value: string) => organizationAPI.update(org.id, { name: value })
    .then(() => { flash('details'); return onReload(); })
    .catch(fail);
  const saveSlug = (value: string) => organizationAPI.update(org.id, { slug: value })
    .then(() => { flash('details'); return onReload(); })
    .catch(fail);
  const saveEmail = (value: string) => organizationAPI.update(org.id, { contact_email: value })
    .then(() => flash('details'))
    .catch(fail);
  const savePhone = (value: string) => organizationAPI.update(org.id, { contact_phone: value })
    .then(() => flash('details'))
    .catch(fail);

  const catalogMode = (org.settings?.catalog_management || 'independent') as string;
  const setCatalogMode = (val: string) => organizationAPI.update(org.id, { settings: { ...(org.settings || {}), catalog_management: val } })
    .then(() => { flash('catalog'); return onReload(); })
    .catch(fail);

  const restricted = org.settings?.restrict_cancellations === true;
  const setRestricted = (next: boolean) => organizationAPI.update(org.id, { settings: { ...(org.settings || {}), restrict_cancellations: next } })
    .then(() => { flash('cancellations'); return onReload(); })
    .catch(fail);

  const deleteOrg = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    try {
      await organizationAPI.delete(org.id);
      await onDeleted();
    } catch (err) {
      showToast('error', serverMessage(err, t('orgDashboard:settings.deleteFailed')));
    } finally {
      setDeleting(false);
    }
  };

  const enterBlurs = (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') e.currentTarget.blur(); };

  return (
    <>
      <SettingsSheet
        eyebrow={t('orgDashboard:nav.groups.org')}
        title={t('orgDashboard:settings.title')}
        subtitle={t('orgDashboard:settings.subtitle')}
      >
        <SheetSection
          id="org-details"
          title={t('admin:orgDetailsSection')}
          hint={t('orgDashboard:settings.detailsHint')}
          aside={<SavedNote show={saved === 'details'} />}
          testId="org-settings-details"
        >
          <div className="xs-grid2">
            <SheetField label={t('admin:orgNameLabel')} htmlFor={`${uid}-name`}>
              <input id={`${uid}-name`} type="text" defaultValue={org.name} onBlur={e => saveName(e.target.value)} onKeyDown={enterBlurs}
                className={INPUT_CLS} data-testid="org-settings-name" />
            </SheetField>
            <SheetField label={t('admin:orgSlugLabel')} htmlFor={`${uid}-slug`} hint={t('orgDashboard:settings.slugHint')}>
              <input id={`${uid}-slug`} type="text" defaultValue={org.slug} onBlur={e => saveSlug(e.target.value)} onKeyDown={enterBlurs}
                className={INPUT_CLS} data-testid="org-settings-slug" />
            </SheetField>
            <SheetField label={t('admin:contactEmailLabel')} htmlFor={`${uid}-email`}>
              <input id={`${uid}-email`} type="email" defaultValue={org.contact_email || ''} onBlur={e => saveEmail(e.target.value)} onKeyDown={enterBlurs}
                className={INPUT_CLS} data-testid="org-settings-email" />
            </SheetField>
            <SheetField label={t('admin:contactPhoneLabel')} htmlFor={`${uid}-phone`}>
              <input id={`${uid}-phone`} type="tel" defaultValue={org.contact_phone || ''} onBlur={e => savePhone(e.target.value)} onKeyDown={enterBlurs}
                className={INPUT_CLS} data-testid="org-settings-phone" />
            </SheetField>
          </div>
        </SheetSection>

        <SheetSection
          id="org-catalog-mode"
          title={t('orgDashboard:settings.catalogTitle')}
          indexLabel={t('orgDashboard:settings.catalogIndex')}
          hint={t('orgDashboard:settings.catalogHint')}
          aside={<SavedNote show={saved === 'catalog'} />}
          testId="org-settings-catalog"
        >
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('orgDashboard:settings.catalogTitle')}>
            {(['independent', 'central'] as const).map(val => (
              <SheetInherit
                key={val}
                name={`${uid}-catalog`}
                checked={catalogMode === val}
                onSelect={() => { if (catalogMode !== val) setCatalogMode(val); }}
                label={t(`orgDashboard:settings.catalog.${val}`)}
                value={t(`orgDashboard:settings.catalog.${val}Hint`)}
                testId={`org-settings-catalog-${val}`}
              />
            ))}
          </div>
        </SheetSection>

        <SheetSection
          id="org-cancellations"
          title={t('orgDashboard:settings.cancelTitle')}
          hint={t('orgDashboard:settings.cancelHint')}
          aside={<SavedNote show={saved === 'cancellations'} />}
          testId="org-settings-cancellations"
        >
          <SwitchRow
            title={t('orgDashboard:settings.cancelSwitch')}
            subtitle={restricted ? t('orgDashboard:settings.cancelOn') : t('orgDashboard:settings.cancelOff')}
            checked={restricted}
            onChange={next => setRestricted(next)}
          />
        </SheetSection>

        {/* Εγχειρίδιο χρήσης — το κατεβάζουν τα ταμεία από το μενού πάνω δεξιά */}
        <OrgManualSection orgId={org.id} />

        {/* Εμφάνιση — προεπιλογή όλων των καταστημάτων (organizations.theme, migration 263)· ΙΔΙΟ component με
            πριν, με δικό του «Αποθήκευση». Η δική του κεφαλίδα κρύβεται (org.css): τον τίτλο τον έχει η ενότητα. */}
        <SheetSection
          id="org-theme"
          title={t('settings:theme.title')}
          hint={t('admin:orgThemeHint')}
          testId="org-settings-theme"
        >
          <div className="org-theme-slot">
            <OrgThemeSettings orgId={org.id} value={org.theme ?? null} storeName={org.name} onSaved={onThemeSaved} />
          </div>
        </SheetSection>

        {isOwner && (
          <SheetSection
            id="org-delete"
            title={t('admin:deleteOrgButton')}
            hint={t('orgDashboard:settings.deleteHint')}
            testId="org-settings-delete"
          >
            <button type="button" className={DANGER_GHOST_BTN} onClick={() => setConfirmDelete(true)} disabled={deleting} data-testid="org-settings-delete-btn">
              {deleting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {t('admin:deleteOrgButton')}
            </button>
          </SheetSection>
        )}
      </SettingsSheet>

      <OrgConfirm
        isOpen={confirmDelete}
        title={t('admin:deleteOrgButton')}
        message={t('admin:deleteOrgConfirm')}
        confirmLabel={t('orgDashboard:settings.deleteConfirm')}
        cancelLabel={t('orgDashboard:actions.cancel')}
        variant="danger"
        onConfirm={deleteOrg}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};

export default OrgSettingsTab;
